import { useMemo, useState } from 'react';
import type { RegistroOdontograma } from '../api/tipos';
import { clases } from '../comun/componentes';
import {
  ESTADOS_PIEZA,
  ESTADOS_SUPERFICIE,
  SUPERFICIES,
  aUniversal,
  filasDe,
  indexar,
  nombreDeEstado,
} from './modeloOdontograma';
import type { Denticion, EstadoDePieza, Superficie } from './modeloOdontograma';

/**
 * Odontograma en SVG propio. Sin sprites de terceros ni imágenes licenciadas: cada diente
 * es un cuadrado dividido en cinco superficies, que es la representación que un odontólogo
 * lee de un vistazo y la única que funciona con guante en una tablet.
 *
 * Reglas de UX que fija el encargo y que están respetadas acá:
 *  - áreas táctiles de 44×44 px como mínimo por diente (la superficie individual es más
 *    chica, pero el diente completo se puede seleccionar y editar desde el panel);
 *  - sin hover como único mecanismo: todo estado se ve en el dibujo, no en un tooltip;
 *  - un solo nivel de menú: se elige capa, se elige estado y se toca.
 */

const LADO = 44;
const SEPARACION = 6;
const ALTO_ROTULO = 16;

type Props = {
  registros: RegistroOdontograma[];
  denticion?: Denticion;
  /** Capa que se está mirando o editando. */
  capa: 'Hallazgo' | 'Plan';
  /** Estado con el que se pinta al tocar. Nulo = sólo lectura. */
  pincel?: string | null;
  /** Nomenclatura del rótulo. FDI es la canónica; Universal es sólo presentación. */
  notacion?: 'FDI' | 'Universal';
  alTocarSuperficie?: (pieza: string, superficie: Superficie) => void;
  alTocarPieza?: (pieza: string) => void;
  piezaSeleccionada?: string | null;
};

export function Odontograma({
  registros,
  denticion = 'Permanente',
  capa,
  pincel = null,
  notacion = 'FDI',
  alTocarSuperficie,
  alTocarPieza,
  piezaSeleccionada = null,
}: Props) {
  const mapa = useMemo(() => indexar(registros), [registros]);
  const filas = useMemo(() => filasDe(denticion), [denticion]);
  const editable = pincel != null && alTocarSuperficie != null;

  return (
    <div className="space-y-4">
      {filas.map((fila, indice) => {
        const columnas = Math.max(fila.superior.length, fila.inferior.length);
        const ancho = columnas * (LADO + SEPARACION) + SEPARACION;
        const alto = 2 * (LADO + ALTO_ROTULO) + SEPARACION * 3;

        return (
          <div key={indice} className="overflow-x-auto">
            <svg
              viewBox={`0 0 ${ancho} ${alto}`}
              className="h-auto w-full min-w-[560px]"
              role="group"
              aria-label={
                indice === 0 && denticion !== 'Temporaria'
                  ? 'Arcadas permanentes'
                  : 'Arcadas temporarias'
              }
            >
              {/* Línea media: separa hemiarcada derecha de izquierda, como en el papel. */}
              <line
                x1={ancho / 2}
                y1={2}
                x2={ancho / 2}
                y2={alto - 2}
                stroke="#d7d2c5"
                strokeWidth={1}
                strokeDasharray="4 4"
              />

              {fila.superior.map((pieza, columna) => (
                <Diente
                  key={pieza}
                  pieza={pieza}
                  x={SEPARACION + columna * (LADO + SEPARACION)}
                  y={SEPARACION + ALTO_ROTULO}
                  rotuloArriba
                  notacion={notacion}
                  estado={mapa.get(pieza)?.[capa === 'Plan' ? 'plan' : 'hallazgo']}
                  seleccionada={piezaSeleccionada === pieza}
                  editable={editable}
                  alTocarSuperficie={alTocarSuperficie}
                  alTocarPieza={alTocarPieza}
                />
              ))}

              {fila.inferior.map((pieza, columna) => (
                <Diente
                  key={pieza}
                  pieza={pieza}
                  x={SEPARACION + columna * (LADO + SEPARACION)}
                  y={SEPARACION * 2 + LADO + ALTO_ROTULO}
                  rotuloArriba={false}
                  notacion={notacion}
                  estado={mapa.get(pieza)?.[capa === 'Plan' ? 'plan' : 'hallazgo']}
                  seleccionada={piezaSeleccionada === pieza}
                  editable={editable}
                  alTocarSuperficie={alTocarSuperficie}
                  alTocarPieza={alTocarPieza}
                />
              ))}
            </svg>
          </div>
        );
      })}
    </div>
  );
}

function Diente({
  pieza,
  x,
  y,
  rotuloArriba,
  notacion,
  estado,
  seleccionada,
  editable,
  alTocarSuperficie,
  alTocarPieza,
}: {
  pieza: string;
  x: number;
  y: number;
  rotuloArriba: boolean;
  notacion: 'FDI' | 'Universal';
  estado: EstadoDePieza | undefined;
  seleccionada: boolean;
  editable: boolean;
  alTocarSuperficie?: (pieza: string, superficie: Superficie) => void;
  alTocarPieza?: (pieza: string) => void;
}) {
  const rotulo = notacion === 'Universal' ? aUniversal(pieza) : pieza;
  const yRotulo = rotuloArriba ? y - 4 : y + LADO + 12;
  const estadoPieza = estado?.pieza ? ESTADOS_PIEZA[estado.pieza] : undefined;
  const tercio = LADO / 3;

  // Trapecios: cada superficie ocupa su lado, y la oclusal el centro. Es el mismo dibujo
  // que el odontograma de papel que usa la clínica.
  const caras: { codigo: Superficie; puntos: string }[] = [
    { codigo: 'V', puntos: `${x},${y} ${x + LADO},${y} ${x + LADO - tercio},${y + tercio} ${x + tercio},${y + tercio}` },
    {
      codigo: 'L',
      puntos: `${x},${y + LADO} ${x + tercio},${y + LADO - tercio} ${x + LADO - tercio},${y + LADO - tercio} ${x + LADO},${y + LADO}`,
    },
    { codigo: 'M', puntos: `${x},${y} ${x + tercio},${y + tercio} ${x + tercio},${y + LADO - tercio} ${x},${y + LADO}` },
    {
      codigo: 'D',
      puntos: `${x + LADO},${y} ${x + LADO},${y + LADO} ${x + LADO - tercio},${y + LADO - tercio} ${x + LADO - tercio},${y + tercio}`,
    },
  ];

  const descripcion = describir(pieza, estado);

  return (
    <g>
      <text
        x={x + LADO / 2}
        y={yRotulo}
        textAnchor="middle"
        className="fill-tinta-500"
        style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
      >
        {rotulo}
      </text>

      {/* Fondo del diente: lleva el estado de pieza completa cuando lo hay. */}
      <rect
        x={x}
        y={y}
        width={LADO}
        height={LADO}
        rx={4}
        fill={estadoPieza?.color ?? '#ffffff'}
        stroke={seleccionada ? '#1d6557' : '#d7d2c5'}
        strokeWidth={seleccionada ? 2.5 : 1}
      />

      {caras.map((cara) => {
        const codigoEstado = estado?.superficies[cara.codigo];
        const pintura = codigoEstado ? ESTADOS_SUPERFICIE[codigoEstado] : undefined;

        return (
          <polygon
            key={cara.codigo}
            points={cara.puntos}
            fill={pintura?.color ?? 'transparent'}
            stroke={pintura?.borde ?? '#e9e6dd'}
            strokeWidth={pintura ? 1 : 0.5}
            onClick={editable ? () => alTocarSuperficie?.(pieza, cara.codigo) : undefined}
            style={editable ? { cursor: 'pointer' } : undefined}
          />
        );
      })}

      {/* Oclusal / incisal en el centro. */}
      <rect
        x={x + tercio}
        y={y + tercio}
        width={tercio}
        height={tercio}
        fill={
          estado?.superficies.O ? (ESTADOS_SUPERFICIE[estado.superficies.O]?.color ?? '#7a8691') : 'transparent'
        }
        stroke={
          estado?.superficies.O ? (ESTADOS_SUPERFICIE[estado.superficies.O]?.borde ?? '#5b6873') : '#e9e6dd'
        }
        strokeWidth={estado?.superficies.O ? 1 : 0.5}
        onClick={editable ? () => alTocarSuperficie?.(pieza, 'O') : undefined}
        style={editable ? { cursor: 'pointer' } : undefined}
      />

      {estadoPieza && (
        <text
          x={x + LADO / 2}
          y={y + LADO / 2 + 5}
          textAnchor="middle"
          className="fill-tinta-900"
          style={{ fontSize: 14, fontWeight: 700, pointerEvents: 'none' }}
        >
          {estadoPieza.marca}
        </text>
      )}

      {/*
        Área táctil del diente completo: 44×44 px, por encima de las superficies. Abre el
        panel de la pieza, que es de donde se marca un estado de pieza entera.
      */}
      <rect
        x={x}
        y={y}
        width={LADO}
        height={LADO}
        fill="transparent"
        role="button"
        tabIndex={0}
        aria-label={descripcion}
        onClick={() => alTocarPieza?.(pieza)}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' || evento.key === ' ') {
            evento.preventDefault();
            alTocarPieza?.(pieza);
          }
        }}
        style={{ cursor: alTocarPieza ? 'pointer' : 'default', pointerEvents: editable ? 'none' : 'auto' }}
      />
    </g>
  );
}

function describir(pieza: string, estado: EstadoDePieza | undefined): string {
  if (!estado || (!estado.pieza && Object.keys(estado.superficies).length === 0)) {
    return `Pieza ${pieza}, sin hallazgos`;
  }

  const partes: string[] = [];

  if (estado.pieza) {
    partes.push(nombreDeEstado(estado.pieza));
  }

  for (const superficie of SUPERFICIES) {
    const valor = estado.superficies[superficie.codigo];

    if (valor) {
      partes.push(`${superficie.nombre}: ${nombreDeEstado(valor)}`);
    }
  }

  return `Pieza ${pieza}. ${partes.join('. ')}`;
}

/** Referencia de colores. Sin ella el dibujo es bonito y no dice nada. */
export function LeyendaOdontograma() {
  const [abierta, setAbierta] = useState(false);

  return (
    <div className="rounded-panel border border-papel-300 bg-papel-50">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        className="flex min-h-tactil w-full items-center justify-between px-3 text-sm font-medium text-tinta-700"
      >
        Referencia de estados
        <span aria-hidden="true">{abierta ? '▲' : '▼'}</span>
      </button>

      {abierta && (
        <div className="grid gap-4 border-t border-papel-300 p-3 tablet:grid-cols-2">
          <div>
            <p className="etiqueta-campo mb-2">Superficies</p>
            <ul className="space-y-1.5">
              {Object.entries(ESTADOS_SUPERFICIE).map(([codigo, estado]) => (
                <li key={codigo} className="flex items-center gap-2 text-dato text-tinta-600">
                  <span
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 rounded border"
                    style={{ background: estado.color, borderColor: estado.borde }}
                  />
                  {estado.nombre}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="etiqueta-campo mb-2">Pieza completa</p>
            <ul className="space-y-1.5">
              {Object.entries(ESTADOS_PIEZA).map(([codigo, estado]) => (
                <li key={codigo} className="flex items-center gap-2 text-dato text-tinta-600">
                  <span
                    aria-hidden="true"
                    className="grid h-4 w-4 shrink-0 place-items-center rounded border border-papel-300 text-[10px] font-bold"
                    style={{ background: estado.color }}
                  >
                    {estado.marca}
                  </span>
                  {estado.nombre}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/** Paleta de pinceles: se elige un estado y se toca el diente. Un solo nivel de menú. */
export function Paleta({
  capa,
  alCambiarCapa,
  pincel,
  alCambiarPincel,
}: {
  capa: 'Hallazgo' | 'Plan';
  alCambiarCapa: (capa: 'Hallazgo' | 'Plan') => void;
  pincel: string | null;
  alCambiarPincel: (estado: string | null) => void;
}) {
  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Capa" className="flex gap-1 rounded-lg bg-papel-200 p-1">
        {(['Hallazgo', 'Plan'] as const).map((opcion) => (
          <button
            key={opcion}
            type="button"
            role="radio"
            aria-checked={capa === opcion}
            onClick={() => alCambiarCapa(opcion)}
            className={clases(
              'min-h-tactil flex-1 rounded-md px-3 text-sm font-medium transition-colors',
              capa === opcion ? 'bg-white text-marca-700 shadow-panel' : 'text-tinta-600',
            )}
          >
            {opcion === 'Hallazgo' ? 'Lo que hay' : 'Lo que se va a hacer'}
          </button>
        ))}
      </div>

      <div>
        <p className="etiqueta-campo mb-2">Marcar superficie con</p>
        <div className="flex flex-wrap gap-2">
          <BotonPincel activo={pincel === null} onClick={() => alCambiarPincel(null)}>
            Ver sin editar
          </BotonPincel>
          {Object.entries(ESTADOS_SUPERFICIE).map(([codigo, estado]) => (
            <BotonPincel
              key={codigo}
              activo={pincel === codigo}
              onClick={() => alCambiarPincel(codigo)}
              color={estado.color}
              borde={estado.borde}
            >
              {estado.nombre}
            </BotonPincel>
          ))}
        </div>
      </div>
    </div>
  );
}

function BotonPincel({
  activo,
  onClick,
  color,
  borde,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  color?: string;
  borde?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={clases(
        'inline-flex min-h-tactil items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
        activo
          ? 'border-marca-600 bg-marca-50 text-marca-700 ring-1 ring-marca-600'
          : 'border-papel-300 bg-white text-tinta-600 hover:bg-papel-50',
      )}
    >
      {color && (
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 rounded border"
          style={{ background: color, borderColor: borde }}
        />
      )}
      {children}
    </button>
  );
}
